import { WatchfaceRenderer } from '..';
import { RendererRuntime } from '../runtime';
import { recolor } from '../utils/color';
import { round } from 'lodash-es';
import { IFTTTAnimation } from '../animation/IFTTTAnimation';
import { toRadian } from '../utils/string.utilts';
import { normalizeCanvasDimensions } from '../utils/canvas.utils';

export abstract class LayoutBase {
  protected name: string;
  protected x: number;
  protected y: number;
  protected offsetX: number;
  protected offsetY: number;
  protected width = 0;
  protected height = 0;
  protected rotation = 0;
  protected rotatable = true;
  protected _scale = 1;
  protected _alpha = 1;
  dataItem: DataItem;

  protected renderer: WatchfaceRenderer;
  protected canvas: HTMLCanvasElement;
  protected ctx: CanvasRenderingContext2D;
  protected runtime: RendererRuntime;

  protected posi: { x: number; y: number; offsetX: number; offsetY: number };

  protected _layoutId: string;

  // slot默认展示项的widget ref（仅slot的layout有）
  protected _parameter?: string;

  private static offScreenMap = new Map<
    string,
    {
      canvas: OffscreenCanvas;
      ctx: OffscreenCanvasRenderingContext2D;
      dirtyParmas?: Record<string, string | number | boolean>;
    }
  >();
  static clearCache() {
    LayoutBase.offScreenMap.clear();
  }

  constructor(
    renderer: LayoutBase['renderer'],
    dataItem: LayoutBase['dataItem'],
    // x y为渲染时的绝对坐标，_x _y为相对父元素的坐标
    posi: LayoutBase['posi'],
    _layoutId: string,
    _scale?: number,
    _alpha?: number,
    _parameter?: string
  ) {
    this.name = dataItem.name ?? Math.random().toString();

    this.x = Number(posi.x);
    this.y = Number(posi.y);
    this.offsetX = Number(posi.offsetX);
    this.offsetY = Number(posi.offsetY);

    this.posi = posi;
    this.dataItem = dataItem;
    this._layoutId = _layoutId;
    this._scale = _scale ?? 1;
    this._alpha = _alpha ?? 1;
    this._parameter = _parameter;

    this.renderer = renderer;
    this.canvas = this.renderer.canvas;
    this.ctx = this.renderer.ctx;
    this.runtime = this.renderer.runtime;
    this.rotatable = this.renderer.options.renderTheme.ignoreRotation
      ? false
      : true;
  }

  abstract getSize(): { width: number; height: number };
  abstract draw(): void;

  protected getValueStartRange() {
    // 起始值
    const node = this.dataItem as IDataItemPointer;

    const valueStart =
      isNaN(Number(node.valueStart)) == false
        ? Number(node.valueStart)
        : (this.getDataSourceValue(String(node.valueStart)) as number);
    // valueRange
    const valueRange =
      isNaN(Number(node.valueRange)) == false
        ? Number(node.valueRange)
        : // valueRange为数据源时，valueRange代表valueEnd，所以valueRange=valueEnd-valueStart
          (this.getDataSourceValue(String(node.valueRange)) as number) -
          valueStart;
    return { valueStart, valueRange };
  }

  /**
   * 通过离屏画布缓存图形，不可涉及旋转、缩放
   * @param size
   * @param init
   * @returns
   */
  protected getOffscreen(
    config: {
      width: number;
      height: number;
      init?: (
        canvas: OffscreenCanvas,
        ctx: OffscreenCanvasRenderingContext2D
      ) => void;
      dirtyParmas?: Record<string, string | number | boolean>;
    },
    key?: string
  ) {
    // 离屏画布缓存需要和主题配色相关
    const {
      colorMode,
      themeColor,
      recolorMode = 'only-image'
    } = this.renderer.options.renderTheme;
    const name = [
      this.renderer.id,
      colorMode,
      themeColor,
      recolorMode,
      this.name,
      key ?? 'key'
    ].join('_');
    const { width, height, init, dirtyParmas = {} } = config;
    // 规范化 Canvas 尺寸
    const { width: canvasWidth, height: canvasHeight } =
      normalizeCanvasDimensions(width, height);
    if (!LayoutBase.offScreenMap.has(name)) {
      const canvas = new OffscreenCanvas(canvasWidth, canvasHeight);
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!canvas || !ctx) throw '初始化offscreen canvas失败';
      init?.(canvas, ctx);
      LayoutBase.offScreenMap.set(name, { canvas, ctx, dirtyParmas });
    }
    const {
      canvas,
      ctx,
      dirtyParmas: lastParams = {}
    } = LayoutBase.offScreenMap.get(name) ?? {};
    if (!canvas || !ctx) throw '初始化offscreen canvas失败';
    // 是否需要重新绘制
    const lastKeys = Object.keys(lastParams);
    const curKeys = Object.keys(dirtyParmas);
    if (
      canvas.width != canvasWidth ||
      canvas.height != canvasHeight ||
      lastKeys.length != curKeys.length ||
      lastKeys.some((key) => {
        return lastParams[key] != dirtyParmas[key];
      })
    ) {
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      init?.(canvas, ctx);
      LayoutBase.offScreenMap.set(name, { canvas, ctx, dirtyParmas });
    }
    return { canvas, ctx };
  }

  /**
   * 获取数据源数据
   * @param sourceKey
   * @param allowType
   * @returns
   */
  protected getDataSourceValue = (sourceKey: string): number | string => {
    const key = sourceKey ?? '';
    const _value = (key: string) => {
      const configDataWhole = this.runtime.configDataWhole;
      return configDataWhole[key];
    };

    // 获取数据源值
    let val: string | number | undefined = _value(key);

    // 根据不同类型添加默认值
    const isString = key.toLowerCase().includes('string');
    val = val ?? (isString ? '' : 0);
    this.renderer.runtime.configData[key] = val;

    if (!this.renderer.options.unAssociateTime) {
      // 时间hour类数据，需要通过时和分进行拼接计算
      if (
        [
          'timeHour',
          'timeHour24H',
          'timeHour12H',
          'weatherCurrentSunRise',
          'weatherCurrentSunSet',
          'dualtimeHour'
        ].includes(key)
      ) {
        const _key =
          key === 'timeHour24H' || key === 'timeHour12H'
            ? 'time'
            : key.endsWith('timeHour')
            ? key.replace('Hour', '')
            : key;
        const _hourKey = _key + 'Hour';
        const _minuteKey = _key + 'Minute';
        const _hour = Math.floor(Number(_value(_hourKey)));
        const _minute = Math.floor(Number(_value(_minuteKey)));
        val = round(_hour + _minute / 60, 3);
      }
      // 分钟hour类数据，需要通过时和分进行拼接计算
      if (['timeMinute', 'dualtimeMinute'].includes(key)) {
        const _secondKey = key.replace('Minute', 'Second');
        const _minute = Math.floor(Number(_value(key)));
        const _second = Math.floor(Number(_value(_secondKey)));
        val = round(_minute + _second / 60, 3);
      }
    }

    return val;
  };

  public getRuntimeState() {
    const _id = this._layoutId ?? '-';
    const name = RendererRuntime.generateStateKey(this.name, _id);
    return this.runtime.state[name];
  }

  protected setRuntimeData(data: Partial<RuntimeState>) {
    const _id = this._layoutId ?? '-';
    const name = RendererRuntime.generateStateKey(this.name, _id);

    const _data = { ...data };
    Object.keys(_data).forEach((key) => {
      const _key = key as keyof typeof _data;
      if (_data[_key] == undefined || _data[_key] == null) delete _data[_key];
    });

    this.runtime.state[name] = {
      ...this.runtime.state[name],
      ..._data,

      // 重置一下数据，确保不被修改
      x: this.x,
      y: this.y,
      offsetX: this.offsetX,
      offsetY: this.offsetY,
      _id: this._layoutId ?? ''
    };
  }

  protected drawRecolorImage(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    imgEl: HTMLImageElement | OffscreenCanvas,
    options?: {
      x?: number;
      y?: number;
      w?: number;
      h?: number;
    }
  ) {
    const {
      x: _x = 0,
      y: _y = 0,
      w: _w = imgEl.width,
      h: _h = imgEl.height
    } = options ?? {};

    const x = Math.floor(_x);
    const y = Math.floor(_y);
    const width = Math.floor(_w);
    const height = Math.floor(_h);

    ctx.drawImage(imgEl, x, y, width, height);

    // 是否需要重着色
    const {
      colorMode,
      themeColor,
      recolorMode = 'only-image'
    } = this.renderer.options.renderTheme;
    if (
      colorMode == 'recolor' &&
      themeColor &&
      recolorMode == 'only-image' &&
      ((this.dataItem as IImage).recolorEnable ||
        (this.dataItem as IDataItemImageNumber).supportRecolor)
    ) {
      // const { width, height } = imgEl;
      recolor(ctx, { x, y, width, height }, themeColor);
    }
  }

  private getAniFrameValues(_from?: IFTTTAnimation['toState']) {
    const from: IFTTTAnimation['toState'] = {
      // x: 0,
      // y: 0,
      alpha: 1,
      toAlpha: 1,
      scale: 1,
      rotation: 0,
      ...(_from ?? {})
    };

    const res: IFTTTAnimation['toState'] = { ...from };

    const types: IFTTTAnimation['type'][] = [
      'position',
      'alpha',
      'rotation',
      'scale'
    ];
    types.forEach((type) => {
      const aniIns = this.runtime.iftttAnis.find(
        (item) => item.shapeId == this._layoutId && item.type == type
      );
      if (!aniIns) return;
      const frameValues = aniIns.getFrameValue(from);

      Object.keys(frameValues).forEach((key) => {
        const _key = key as keyof typeof frameValues;
        const _val = frameValues[_key];
        if (_val != undefined) {
          res[_key] = _val;
        }
      });
    });

    return res;
  }

  // 定义一个获取旋转中心的函数
  protected getRotatePivot() {
    // 旋转中心在中间的元素
    // 经确认，绑定了数据源的左上角旋转，没有绑定数据源的中心旋转
    const tagsCenter: IDataItemBase['tagType'][] = ['Image', 'Sprite'];
    if (tagsCenter.includes(this.dataItem.tagType)) {
      const { width, height } = this.getSize();
      return { x: width / 2, y: height / 2 };
    } else {
      return { x: 0, y: 0 };
    }
  }

  protected drawAniFrame(
    offScreenCanvas: OffscreenCanvas,
    options?: {
      selfOffset?: { offsetX?: number; offsetY?: number };
      selfRotation?: {
        /** 旋转值：弧度 */
        rotation: number;
        pivotX: number;
        pivotY: number;
      };
    }
  ) {
    const ctx = this.ctx;
    const { width, height } = offScreenCanvas;
    if (!width || !height) return;

    const { selfOffset, selfRotation } = options ?? {};

    const frameValues = this.getAniFrameValues();

    const {
      scale = 1,
      rotation = 0,
      alpha = 1,
      toAlpha = 1,
      x: translate_x = 0,
      y: translate_y = 0
    } = frameValues;

    const positionX = this.x + translate_x;
    const positionY = this.y + translate_y;

    // 原来自带的旋转，如指针等
    if (selfRotation && selfRotation.rotation) {
      const { rotation, pivotX, pivotY } = selfRotation;
      const centerPoint = { x: positionX + pivotX, y: positionY + pivotY };
      ctx.save();
      ctx.translate(centerPoint.x, centerPoint.y); //默认从00开始旋转，这里设置中心点，绘制到中心点位置
      ctx.rotate(rotation); //开始顺时针方向旋转角度，rotation为旋转角度。
      ctx.translate(-centerPoint.x, -centerPoint.y); //画布平移回来
    }

    ctx.save();
    // IFTTT旋转（非元素本身旋转）
    if (rotation) {
      // 关于旋转中心
      // 目前有两个旋转中心规则： https://xiaomi.f.mioffice.cn/docx/doxk4XkDHBcbXqx7Sb7ZUrbk8me
      const { x: pivotX, y: pivotY } = this.getRotatePivot();

      const pivotPoint = {
        x: positionX + pivotX,
        y: positionY + pivotY
      };
      ctx.translate(pivotPoint.x, pivotPoint.y); //默认从00开始旋转，这里设置中心点，绘制到中心点位置
      ctx.rotate(toRadian(rotation)); //开始顺时针方向旋转角度，rotation为旋转角度。
      ctx.translate(-pivotPoint.x, -pivotPoint.y); //画布平移回来
    }

    // 透明度
    if (alpha < 1 || toAlpha < 1) {
      ctx.globalAlpha = alpha;
    }

    // 缩放和位置
    // 关于缩放中心
    // 目前不支持自定义，统一为元素左上角
    const scalePivotX = 0;
    const scalePivotY = 0;
    // 元素位置偏移量
    const { offsetX = 0, offsetY = 0 } = selfOffset ?? {};
    const x = positionX - scalePivotX * (scale - 1) + offsetX;
    const y = positionY - scalePivotY * (scale - 1) + offsetY;

    // position
    ctx.drawImage(
      offScreenCanvas,
      0,
      0,
      width,
      height,
      x,
      y,
      width * scale,
      height * scale
    );
    ctx.globalAlpha = 1;
    ctx.restore();

    return frameValues;
  }

  protected condition(data: ICondition) {
    const paramValue = (param: ICompareParam) => {
      const { value, source, operators } = param;
      const _value = Number(value);
      const value1 = Number(
        Number.isNaN(_value) ? this.getDataSourceValue(String(value)) : _value
      );
      let value2 = Number(this.getDataSourceValue(source));

      if (isNaN(value1) || isNaN(value2)) return false;

      if (operators == '==') {
        return value2 == value1;
      } else if (operators == '&lt;') {
        return value2 < value1;
      } else if (operators == '&lt;=') {
        return value2 <= value1;
      } else if (operators == '>') {
        return value2 > value1;
      } else if (operators == '>=') {
        return value2 >= value1;
      } else {
        return false;
      }
    };
    const logicOr = data.logic_op ?? 'and';
    const compareRes = data.children.map((compare) => {
      const logicOr = compare.logic_op ?? 'and';
      const resArr =
        compare.children?.map((param) => {
          return paramValue(param);
        }) ?? [];

      return logicOr == 'and'
        ? resArr.every((item) => !!item)
        : resArr.some((item) => !!item);
    });
    const res =
      logicOr == 'and'
        ? compareRes.every((item) => !!item)
        : compareRes.some((item) => !!item);

    return res;
  }

  public getLayoutId() {
    return this._layoutId;
  }

  public getLayoutPosi() {
    return this.posi;
  }
}
